import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Button } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { from } from 'rxjs';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { AuthService } from '@/core/auth/auth.service';
import { CertificadoInscricaoPjService } from '@/core/api/sicom-api/registro/prestador/relatorios/certificado-inscricao-pj/certificado-inscricao-pj.service';
import { ReportCertificadoInscricaoPjRequest, ReportCertificadoInscricaoPjResponse } from '@/core/api/sicom-api/registro/prestador/relatorios/certificado-inscricao-pj/certificado-inscricao-pj.model';
import { CertificadoInscricaoPjJobService } from '@/core/services/certificado-inscricao-pj-job.service';
import { UsuarioService } from '@/core/api/sicom-api/sistema/usuario/usuario.service';
import { SolicitanteSearchResponse } from '@/core/api/sicom-api/sistema/usuario/usuario.model';
import { PessoaService } from '@/shared/services/pessoa.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ReportCertificadoInscricaoPjRequest;
type Resp = ReportCertificadoInscricaoPjResponse;

/** Controles tipados do formulário da Certidão de Inscrição PJ. */
interface CertificadoInscricaoPjFormControls {
    idAssinante: FormControl<number | null>;
    assinanteObj: FormControl<SolicitanteSearchResponse | null>;
}

@Component({
    selector: 'app-certificado-inscricao-pj',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, ReactiveFormsModule, SelectEstado, RelatorioContainer, SelectModule, CanDirective],
    templateUrl: './certificado-inscricao-pj.html',
    styleUrl: './certificado-inscricao-pj.scss'
})
/**
 * Relatório PDF assíncrono: Certidão de Inscrição PJ (prestador).
 * Herda `RelatorioFormBase`: export/polling/download/erro vêm da base; aqui só resolvemos a
 * identificação do prestador (via `PessoaService`) e o assinante (UF + solicitante do usuário logado).
 */
export class ReportCertificadoInscricaoPjComponent extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.prestadores.relatorios.gerar;

    private readonly formBuilder = inject(FormBuilder);
    private readonly certificadoService = inject(CertificadoInscricaoPjService);
    private readonly usuarioService = inject(UsuarioService);
    private readonly authService = inject(AuthService);
    private readonly pessoaService = inject(PessoaService);
    readonly certificadoJobService = inject(CertificadoInscricaoPjJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.certificadoJobService.job$;

    ufAssinante = '';
    ufAssinanteTocada = false;

    assinantes = signal<SolicitanteSearchResponse[]>([]);
    loadingAssinantes = signal(false);
    usuarioLogadoId = '';

    private usuarioCpf = '';

    form: FormGroup<CertificadoInscricaoPjFormControls> = this.formBuilder.group<CertificadoInscricaoPjFormControls>({
        idAssinante: new FormControl<number | null>(null, Validators.required),
        assinanteObj: new FormControl<SolicitanteSearchResponse | null>(null, Validators.required)
    });

    protected get jobFacade(): RelatorioJobFacade {
        return this.certificadoJobService;
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.inicializarUsuarioLogado();
    }

    private inicializarUsuarioLogado(): void {
        from(this.authService.getUserInfo()).subscribe((userInfo) => {
            this.usuarioLogadoId = userInfo.userId;
            this.usuarioCpf = userInfo.cpf || '';

            if (this.usuarioCpf) {
                this.buscarAssinantePorUf(this.ufAssinante);
            }
        });
    }

    private buscarAssinantePorUf(uf: string): void {
        if (!this.usuarioCpf) return;

        this.loadingAssinantes.set(true);
        this.usuarioService.buscarSolicitante(this.usuarioCpf, uf, 0, 10).subscribe({
            next: (page) => {
                this.assinantes.set(page.content);
                if (page.content.length === 1) {
                    const solicitante = page.content[0];
                    this.ufAssinante = solicitante.uf;
                    this.form.patchValue({
                        assinanteObj: solicitante,
                        idAssinante: solicitante.idSolicitante
                    });
                }
                this.loadingAssinantes.set(false);
            },
            error: () => this.loadingAssinantes.set(false)
        });
    }

    onAssinanteChange(assinante: SolicitanteSearchResponse | null): void {
        if (assinante) {
            this.ufAssinante = assinante.uf;
            this.form.patchValue({ idAssinante: assinante.idSolicitante });
        } else {
            this.form.patchValue({ idAssinante: null });
        }
    }

    alterarUfAssinante(uf: string | null): void {
        this.ufAssinante = uf ?? '';
        this.form.patchValue({ assinanteObj: null, idAssinante: null });
        this.assinantes.set([]);

        if (this.ufAssinante) {
            this.buscarAssinantePorUf(this.ufAssinante);
        }
    }

    marcarUfAssinanteTocada(): void {
        this.ufAssinanteTocada = true;
    }

    /** Monta o request a partir do prestador selecionado e do assinante; retorna null (e avisa) se faltar dado. */
    protected buildRequest(): Req | null {
        this.form.markAllAsTouched();
        this.ufAssinanteTocada = true;

        if (this.form.invalid || !this.ufAssinante) return null;

        const { idAssinante } = this.form.getRawValue();

        const pessoa = this.pessoaService.getPessoa();
        const pessoaReference = this.pessoaService.getPessoaReference();
        const nuCrm = Number(pessoa?.dadosGerais?.crm || pessoa?.crm || pessoa?.registro || 0);
        const sgUf = pessoa?.uf || pessoaReference?.uf || '';

        if (!nuCrm || !sgUf) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione um prestador antes de gerar a certidão.'
            });
            return null;
        }

        const assinanteObj = this.form.getRawValue().assinanteObj;

        return {
            nuCrm,
            sgUf,
            assinante: assinanteObj?.nmSolicitante || '',
            idAssinante: idAssinante ?? undefined,
            sgUfAssinante: this.ufAssinante
        };
    }

    protected exportar(request: Req) {
        return this.certificadoService.exportarPdf(request);
    }

    limparFormulario(): void {
        const assinante = this.form.getRawValue().assinanteObj;
        this.form.reset({ idAssinante: null, assinanteObj: null });

        // Mantém o assinante logado se ele existir.
        if (assinante) {
            this.form.patchValue({ assinanteObj: assinante, idAssinante: assinante.idSolicitante });
        }

        this.ufAssinanteTocada = false;
        this.limparJob();
    }
}
